/**
 * De balk die zegt dat een platformbeheerder meekijkt.
 *
 * Staat boven álles, is niet weg te klikken, en blijft staan zolang de sessie een
 * impersonatie is. Dat is met opzet onhandig. Een beheerder die vergeet dat hij nog
 * in de omgeving van een klant zit, wijzigt daar iets in naam van die klant — en
 * anders dan bij een gewone vergissing staat er dan achteraf in de auditlog van die
 * klant dat de klant het zelf deed, met de beheerder ernaast. Deze balk kost een
 * paar regels schermruimte en voorkomt precies dat (§17.38).
 *
 * De tekst loopt wél via de vertaalcatalogus van het dashboard, ook al is de lezer
 * een platformbeheerder. Regel 6 kent de uitzondering "wordt niet vertaald" toe aan
 * de beheeromgeving als APPLICATIE, en dit is een component van het dashboard. Twee
 * regels Nederlands zijn goedkoper dan een uitzondering die iemand later moet
 * uitzoeken.
 */
import { useState } from 'react'
import type { ReactElement } from 'react'

import { stopImpersonation } from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'
import { useSession } from '../lib/session-context'

/** Waar de beheeromgeving staat; zie de tegenhanger in web-admin. */
function adminUrl(): string {
  const raw: unknown = import.meta.env.VITE_ADMIN_BASE_URL
  const configured = typeof raw === 'string' ? raw.trim() : ''
  return configured === '' ? '/admin/' : configured
}

export function ImpersonationBanner(): ReactElement | null {
  const t = useTranslate()
  const { impersonated, user } = useSession()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!impersonated || user === null) {
    return null
  }

  async function stop(): Promise<void> {
    setBusy(true)
    setError(null)

    const result = await stopImpersonation()
    if (!result.ok) {
      setBusy(false)
      setError(t('dashboard.impersonation.stopFailed'))
      return
    }

    // Het token gaat mee in de fragmentidentifier: de beheeromgeving wisselt het daar
    // in voor zijn eigen sessie. Een cookie kan niet mee tussen twee hostnamen
    // (§17.43). Alles achter het hekje bereikt de server niet en belandt dus niet in
    // logboeken.
    //
    // Een volledige navigatie en geen router-push, want de hele applicatiestaat hoort
    // bij de klant en niet bij de beheerder.
    const base = adminUrl().replace(/\/+$/, '')
    window.location.replace(`${base}/handover#${encodeURIComponent(result.data)}`)
  }

  return (
    <div className="impersonation" role="status">
      <span>
        <strong>{t('dashboard.impersonation.label')}</strong>{' '}
        {/* De naam is data en gaat als waarde de zin in; alleen de omhullende zin
            komt uit de catalogus. */}
        {t('dashboard.impersonation.body', {
          name: user.displayName === '' ? user.email : user.displayName,
        })}
        {error === null ? null : ` ${error}`}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          void stop()
        }}
      >
        {busy ? t('dashboard.impersonation.stopping') : t('dashboard.impersonation.stop')}
      </button>
    </div>
  )
}
